import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BatteryTypesSection = () => {
  const [selectedType, setSelectedType] = useState('grp-wall-battery');

  const batteryTypes = [
    {
      id: 'lithium-ion',
      name: 'Lithium-ion (NMC)',
      description: 'Meest gebruikte technologie met hoge energiedichtheid',
      pros: ['Hoge capaciteit', 'Compact formaat', 'Bewezen technologie', 'Goede levensduur'],
      cons: ['Hogere aanschafprijs', 'Temperatuurgevoelig'],
      capacity: '5-20 kWh',
      cycles: '6.000-10.000',
      efficiency: '90-95%',
      price: '€6.000-€12.000',
      image: 'https://images.unsplash.com/photo-1620714223084-8fcacc6dfd8d?w=400&h=300&fit=crop',
      alt: 'Moderne lithium-ion thuisbatterij installatie met hoge energiedichtheid'
    },
    {
      id: 'lfp',
      name: 'Lithium IJzerfosfaat (LFP)',
      description: 'Veiligere variant met langere levensduur',
      pros: ['Zeer veilig', 'Langste levensduur', 'Stabiel', 'Milieuvriendelijker'],
      cons: ['Lagere energiedichtheid', 'Groter formaat'],
      capacity: '5-15 kWh',
      cycles: '10.000-15.000',
      efficiency: '92-96%',
      price: '€7.000-€13.000',
      image: 'https://images.unsplash.com/photo-1509390144185-c6b5e1b73a6f?w=400&h=300&fit=crop',
      alt: 'LFP batterij systeem met extra veiligheidskenmerken en lange levensduur'
    },
    {
      id: 'grp-wall-battery',
      name: 'GRP Wall Battery',
      description: '🏆 Beste keuze voor klanten: Revolutionaire grafeen-gebaseerde technologie met ongekende 20.000 cycli levensduur',
      pros: [
        '20.000 cycli - 2x langer dan traditionele batterijen',
        'Ultrasnelle laadtijd dankzij grafeen technologie',
        'Hoogste energiedichtheid op de markt',
        'Uitstekende temperatuurbestendigheid (-20°C tot +60°C)',
        'Duurzaam en 100% milieuvriendelijk',
        'Beste prijs-kwaliteit verhouding op lange termijn'
      ],
      cons: ['Premium investering met superieure ROI'],
      capacity: '10-25 kWh',
      cycles: '20.000',
      efficiency: '95-98%',
      price: '€9.000-€16.000',
      image: '/assets/images/GRP-Wall-1783594197902.png',
      alt: 'GRP Wall Battery grafeen batterij met 20.000 cycli levensduur - de beste oplossing voor thuisbatterij opslag',
      recommended: true
    }
  ];

  const currentBattery = batteryTypes?.find(b => b?.id === selectedType);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Batterij technologieën vergeleken
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Kies de technologie die het beste bij uw situatie past
          </p>
        </motion.div>

        {/* Type Selector */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {batteryTypes?.map((type) => (
            <motion.button
              key={type?.id}
              onClick={() => setSelectedType(type?.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-6 py-3 rounded-lg font-medium transition-all relative ${
                selectedType === type?.id
                  ? 'bg-green-600 text-white shadow-elevation-2'
                  : 'bg-white text-foreground shadow-elevation-1 hover:shadow-elevation-2'
              } ${
                type?.recommended ? 'ring-2 ring-green-500 ring-offset-2' : ''
              }`}
            >
              {type?.recommended && (
                <span className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full shadow-md">
                  🏆 BESTE KEUZE
                </span>
              )}
              {type?.name}
            </motion.button>
          ))}
        </div>

        {/* Selected Battery Details */}
        <motion.div
          key={selectedType}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12"
        >
          {/* Image and Description */}
          <div>
            {currentBattery?.recommended && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl p-6 mb-6 shadow-elevation-3"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-yellow-400 rounded-full p-3 flex-shrink-0">
                    <Icon name="Award" size={32} className="text-green-900" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold mb-2">🏆 Beste Oplossing voor Klanten</h4>
                    <p className="text-green-50 mb-3">
                      De GRP Wall Battery met grafeen technologie biedt een ongekende <strong className="text-yellow-300">20.000 cycli</strong> levensduur - 
                      dat is 2 tot 4 keer langer dan traditionele batterijen!
                    </p>
                    <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                      <p className="text-sm text-green-50">
                        <strong>Rekenvoorbeeld:</strong> Bij dagelijks gebruik gaat deze batterij <strong className="text-yellow-300">meer dan 54 jaar</strong> mee. 
                        Dat betekent een investering voor het leven met de beste prijs-kwaliteit verhouding op de markt.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            <div className="relative rounded-xl overflow-hidden shadow-elevation-2 mb-6">
              <Image
                src={currentBattery?.image}
                alt={currentBattery?.alt}
                className="w-full h-80 object-cover"
              />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-3">{currentBattery?.name}</h3>
            <p className="text-muted-foreground mb-6">{currentBattery?.description}</p>

            {/* Specs Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-elevation-1">
                <p className="text-sm text-muted-foreground mb-1">Capaciteit</p>
                <p className="text-lg font-bold text-foreground">{currentBattery?.capacity}</p>
              </div>
              <div className={`rounded-lg p-4 shadow-elevation-1 ${
                currentBattery?.recommended 
                  ? 'bg-gradient-to-br from-green-50 to-green-100 ring-2 ring-green-500' :'bg-white'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm text-muted-foreground">Levensduur</p>
                  {currentBattery?.recommended && (
                    <Icon name="TrendingUp" size={16} className="text-green-600" />
                  )}
                </div>
                <p className={`text-lg font-bold ${
                  currentBattery?.recommended ? 'text-green-700' : 'text-foreground'
                }`}>
                  {currentBattery?.cycles} cycli
                </p>
                {currentBattery?.recommended && (
                  <p className="text-xs text-green-600 font-semibold mt-1">
                    5-8x langer! 🚀
                  </p>
                )}
              </div>
              <div className="bg-white rounded-lg p-4 shadow-elevation-1">
                <p className="text-sm text-muted-foreground mb-1">Efficiëntie</p>
                <p className="text-lg font-bold text-foreground">{currentBattery?.efficiency}</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-elevation-1">
                <p className="text-sm text-muted-foreground mb-1">Prijsindicatie</p>
                <p className="text-lg font-bold text-foreground">{currentBattery?.price}</p>
              </div>
            </div>
          </div>

          {/* Pros and Cons */}
          <div>
            <div className="bg-white rounded-xl p-6 shadow-elevation-1 mb-6">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="CheckCircle" size={24} className="text-success" />
                <h4 className="text-lg font-semibold text-foreground">Voordelen</h4>
              </div>
              <ul className="space-y-3">
                {currentBattery?.pros?.map((pro, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{pro}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-elevation-1">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="AlertCircle" size={24} className="text-warning" />
                <h4 className="text-lg font-semibold text-foreground">Aandachtspunten</h4>
              </div>
              <ul className="space-y-3">
                {currentBattery?.cons?.map((con, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="Minus" size={20} className="text-warning mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{con}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-xl shadow-elevation-2 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Eigenschap</th>
                  {batteryTypes?.map((type) => (
                    <th key={type?.id} className={`px-6 py-4 text-left text-sm font-semibold ${
                      type?.recommended 
                        ? 'bg-green-600 text-white relative' :'text-foreground'
                    }`}>
                      {type?.recommended && (
                        <span className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full shadow-md whitespace-nowrap">
                          🏆 AANBEVOLEN
                        </span>
                      )}
                      <span className={type?.recommended ? 'block mt-2' : ''}>{type?.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-muted-foreground">Capaciteit</td>
                  {batteryTypes?.map((type) => (
                    <td key={type?.id} className={`px-6 py-4 text-sm ${
                      type?.recommended ? 'bg-green-50 font-semibold text-foreground' : 'text-foreground'
                    }`}>{type?.capacity}</td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-muted-foreground">Levensduur</td>
                  {batteryTypes?.map((type) => (
                    <td key={type?.id} className={`px-6 py-4 text-sm ${
                      type?.recommended 
                        ? 'bg-green-50 font-bold text-green-700' :'text-foreground'
                    }`}>
                      {type?.cycles}
                      {type?.recommended && <span className="ml-2 text-xs">🚀</span>}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-muted-foreground">Efficiëntie</td>
                  {batteryTypes?.map((type) => (
                    <td key={type?.id} className={`px-6 py-4 text-sm ${
                      type?.recommended ? 'bg-green-50 font-semibold text-foreground' : 'text-foreground'
                    }`}>{type?.efficiency}</td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-muted-foreground">Prijsindicatie</td>
                  {batteryTypes?.map((type) => (
                    <td key={type?.id} className={`px-6 py-4 text-sm font-semibold ${
                      type?.recommended ? 'bg-green-50 text-foreground' : 'text-foreground'
                    }`}>{type?.price}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BatteryTypesSection;